/**
 * Token lifecycle for the suite.
 *
 * The access token lives in this module's closure and nowhere else — not in
 * localStorage, not in sessionStorage, not on `window`. It dies with the tab.
 * The refresh token is an HttpOnly cookie the backend already sets
 * (`Secure; SameSite=Strict; Path=/`), which the browser attaches to
 * same-origin `/auth/refresh` calls without any help from us.
 *
 * That combination is what makes a page reload survivable: the access token is
 * gone, so `bootstrap()` spends the cookie once on startup to get a new one.
 *
 * Refresh is single-flight. The Vaadin `SessionApiManager` learned this the
 * hard way — a view firing eight parallel requests produces eight 401s, and
 * eight naive refreshes would rotate the cookie out from under each other,
 * leaving seven callers holding an already-invalidated token. Every caller
 * here awaits the same in-flight promise instead.
 */

export class AuthenticationFailedError extends Error {
  constructor(message = "Authentication failed") {
    super(message);
    this.name = "AuthenticationFailedError";
  }
}

export interface SessionOptions {
  /** e.g. `/api/2.0` — the same-origin path nginx proxies to the backend. */
  basePath: string;
  /** Called when the session is unrecoverable and the user must log in again. */
  onSessionLost?: () => void;
}

export class Session {
  private accessToken: string | null = null;
  private refreshInFlight: Promise<string> | null = null;

  constructor(private readonly options: SessionOptions) {}

  get basePath(): string {
    return this.options.basePath;
  }

  get token(): string | null {
    return this.accessToken;
  }

  get isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  /**
   * Exchanges credentials for an access token. The backend expects HTTP Basic
   * on this one endpoint (not a JSON body) and answers with the raw JWT as
   * text, setting the refresh cookie on the way out.
   */
  async login(username: string, password: string): Promise<void> {
    const credentials = btoa(`${username}:${password}`);
    const response = await fetch(`${this.options.basePath}/auth/login`, {
      method: "POST",
      headers: { Authorization: `Basic ${credentials}` },
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new AuthenticationFailedError(
        response.status === 401 ? "Invalid username or password" : `Login failed (${response.status})`,
      );
    }

    this.accessToken = (await response.text()).trim();
  }

  /**
   * Recovers a session from the refresh cookie on app start. Returns false when
   * there is no usable cookie, which simply means "show the login screen" — not
   * an error worth surfacing.
   */
  async bootstrap(): Promise<boolean> {
    try {
      await this.refresh();
      return true;
    } catch {
      this.accessToken = null;
      return false;
    }
  }

  /** Single-flight: concurrent callers share one network round-trip. */
  refresh(): Promise<string> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    const attempt = (async () => {
      const response = await fetch(`${this.options.basePath}/auth/refresh`, {
        method: "GET",
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new AuthenticationFailedError(`Refresh rejected (${response.status})`);
      }

      const token = (await response.text()).trim();
      if (!token) {
        throw new AuthenticationFailedError("Refresh returned an empty token");
      }

      this.accessToken = token;
      return token;
    })();

    // Cleared in a `finally` rather than on success only, so a failed refresh
    // doesn't wedge every later attempt behind a permanently rejected promise.
    this.refreshInFlight = attempt.finally(() => {
      this.refreshInFlight = null;
    }) as Promise<string>;

    return this.refreshInFlight;
  }

  async logout(): Promise<void> {
    this.accessToken = null;
    try {
      await fetch(`${this.options.basePath}/auth/logout`, {
        method: "POST",
        credentials: "same-origin",
      });
    } catch {
      // Best effort. The local token is already gone, which is what matters to
      // this tab; the server row expires on its own if the call didn't land.
    }
  }

  /** Drops local state and notifies the app, without calling the backend. */
  abandon(): void {
    this.accessToken = null;
    this.options.onSessionLost?.();
  }
}
